import type { FlowConfig } from './types'
import { config } from '@/src/lib/config/api-config'
import type { AdmissionFieldValue, AdmissionSavePayload } from '@/src/types/api.types'

/**
 * Format a date value to YYYY-MM-DD format for API submission.
 * Uses local time components to avoid UTC timezone shift (which can cause off-by-one day errors).
 */
function formatDateForAPI(value: unknown): string {
  if (!value) return ''

  // If it's already a string in YYYY-MM-DD format, return as is
  if (typeof value === 'string') {
    if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return value

    // Try to parse and reformat
    const parsed = new Date(value)
    if (!isNaN(parsed.getTime())) {
      return toLocalDateString(parsed)
    }
    return value
  }

  // If it's a Date object, convert to YYYY-MM-DD
  if (value instanceof Date) {
    return toLocalDateString(value)
  }

  // Try to parse as date
  const dateStr = String(value)
  const parsed = new Date(dateStr)
  if (!isNaN(parsed.getTime())) {
    return toLocalDateString(parsed)
  }

  return dateStr
}

/** Extract YYYY-MM-DD from a Date using local timezone (not UTC) */
function toLocalDateString(date: Date): string {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

/**
 * Map form field keys to API field names
 */
const FIELD_NAME_MAP: Record<string, string> = {}

/**
 * Transform form data organized by step into the API submission format.
 *
 * Input: { step1: { field1: val1, ... }, step2: { field2: val2, ... }, ... }
 * Output: { id, step, finalStep, values: [...] }
 *
 * @param formData Form data organized by step
 * @param flow Flow configuration
 * @param currentStepNumber Current step number
 * @param finalStep Whether this is the final submission
 * @param draftId Optional existing draft ID
 * @param lang Optional language code for the submission
 */
export function transformFormDataToPayload(
  formData: Record<string, unknown>,
  flow: FlowConfig,
  currentStepNumber: number,
  finalStep: boolean,
  draftId: string | null = null,
  lang?: string
): AdmissionSavePayload {
  const values: AdmissionFieldValue[] = []

  // Get flattened form data
  const flatFormData: Record<string, unknown> = Object.values(formData).reduce<
    Record<string, unknown>
  >((acc, stepData) => {
    if (stepData && typeof stepData === 'object') {
      return { ...acc, ...(stepData as Record<string, unknown>) }
    }
    return acc
  }, {})

  // Add lang field if provided
  if (lang) {
    values.push({
      name: 'lang',
      value: lang
    })
  }

  // Add occupazione field if present
  if (flatFormData['occupazione'] !== undefined) {
    values.push({
      name: 'occupazione',
      value: String(flatFormData['occupazione'])
    })
  }

  // Add lista_attesa field (from richiestaListaAttesa)
  const listaAttesa = flatFormData['richiestaListaAttesa'] === true
  values.push({
    name: 'lista_attesa',
    value: String(listaAttesa)
  })

  const currentFlowStep = flow.steps.find((step) => step.id === currentStepNumber)
  const payloadStep = currentFlowStep?.saveStep ?? currentStepNumber

  // Iterate through all steps up to and including the current step
  for (const step of flow.steps) {
    if (step.id > currentStepNumber) break

    const stepKey = `step${step.id}`
    const stepData = (formData[stepKey] as Record<string, unknown>) ?? {}

    // Iterate through all fields in this step
    for (const field of step.fields) {
      const fieldValue = stepData[field.key]

      // Skip fields with no value
      if (fieldValue === undefined || fieldValue === null || fieldValue === '') {
        continue
      }

      // Skip special fields that are handled separately
      if (field.key === 'richiestaListaAttesa') {
        continue
      }

      // Map field name to API field name
      const apiFieldName = FIELD_NAME_MAP[field.key] ?? field.key

      // Format value: dates should be YYYY-MM-DD
      let formattedValue: string
      if (field.type === 'date-picker-airbnb') {
        formattedValue = formatDateForAPI(fieldValue)
      } else {
        formattedValue = String(fieldValue)
      }

      // Create the field value entry
      const fieldEntry: AdmissionFieldValue = {
        name: apiFieldName,
        value: formattedValue
      }

      values.push(fieldEntry)

      // Special case: bando_1 also gets copied as bandopnrr
      if (field.key === 'bando') {
        values.push({
          name: 'bandopnrr',
          value: formattedValue
        })
      }
    }
  }

  const payload: AdmissionSavePayload = {
    id: draftId ?? null,
    step: payloadStep,
    finalStep,
    test: config.admissionsSaveTest,
    values
  }

  console.log('🔄 [transformFormDataToPayload]', {
    currentStep: currentStepNumber,
    finalStep,
    valuesCount: values.length,
    payload
  })

  return payload
}
